/**
 * Named sizes for interactive controls (pills, inputs, icon buttons, steppers).
 * Prefer these over ad-hoc pixel values so we can tune a single place when
 * adjusting density for mobile/tablet.
 */

import { spacing } from "./spacing";

export const controlSize = {
  // Circular / pill-shaped selectable atoms (day pill, chip pill).
  pill: {
    sm: "28px",
    md: "32px",
    lg: "40px",
  },
  /**
   * Weekday pills in a schedule "Repeat on" row — Figma 36px, which sits
   * between `pill.md` and `pill.lg`.
   *
   * Deliberately a sibling of `pill` rather than a fourth key inside it:
   * `ControlSizeKey` is derived from `pill`, so adding it there would leak
   * "day" into every field / stepper / icon-button size prop.
   *
   * In `rem` per the units rule; the px entries above predate it, and
   * converting them touches every consumer — flagged, not done here.
   */
  dayPill: "2.25rem",
  /** 34px pill / chip height; sits between `pill.md` (32px) and `dayPill` (36px). Sibling of `pill` for the same reason as `dayPill`. */
  pillTall: "2.125rem",
  /** 18px tall inline badge (pill's count / scope tag). */
  badge: "1.125rem",
  /** Tab-strip tab height (48px). */
  tab: "3rem",
  // Rectangular fields: text input, dropdown trigger, date field, number stepper.
  field: {
    sm: "32px",
    md: "40px",
    lg: "48px",
  },
  // Icon-only buttons (chevrons, close, delete).
  iconButton: {
    sm: "24px",
    md: "32px",
    lg: "40px",
  },
  /**
   * Config-driven forms (connector wizard credentials): Figma uses one row
   * height for text inputs, segmented mode toggles, and file triggers.
   */
  configForm: {
    controlHeight: spacing[11],
  },
} as const;

/** Reading widths of the data app header band: title and intro. */
export const headerMaxWidth = {
  title: "53.75rem", // 860px
  intro: "48.75rem", // 780px
} as const;

export type ControlCategory = keyof typeof controlSize;
export type ControlSizeKey = keyof (typeof controlSize)["pill"];

/** Pill sizes plus the schedule-specific 36px weekday diameter. */
export type PillSizeKey = ControlSizeKey | "dayPill";

export const pillDiameter = (size: PillSizeKey): string =>
  size === "dayPill" ? controlSize.dayPill : controlSize.pill[size];
